import { useEffect, useState } from 'react';
import type { AtWorkDoor } from '@conai/shared-types';
import { useI18n } from '../../../i18n';

interface Props {
  door: AtWorkDoor;
  isSubmitting: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

export function ProvideAccessModal({ door, isSubmitting, onConfirm, onClose }: Props) {
  const [reason, setReason] = useState('');
  const { t } = useI18n();

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(reason.trim());
  };

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 z-40 bg-black/50" onClick={isSubmitting ? undefined : onClose} aria-hidden="true" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-bold text-slate-900">{t('openTemporarily')}</h2>
            <p className="mt-1 text-sm text-slate-500">
              Sending temporary unlock command for door <span className="font-semibold text-slate-700">{door.name}</span>
              {' '}({door.site_name}). The door will automatically lock back after the configured duration.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-4">
            <div>
              <label htmlFor="atwork-reason" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('reasonOptional')}
              </label>
              <input
                id="atwork-reason"
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('reasonPlaceholder')}
                maxLength={255}
                autoFocus
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {isSubmitting && (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                )}
                {isSubmitting ? t('sendUnlockCommand') : t('openTemporarily')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
